import { useState } from "react";
import { track } from "@/lib/analytics";
import { Link } from "react-router-dom";
import { X, ArrowCounterClockwise, Leaf } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DIET_LABELS, SHORT_LABELS, dietHref } from "@/lib/diets";
import DietCopyPanel from "@/components/DietCopyPanel";

const SHORT_WHY = {
  vegan: "vegan", vegetarian: "vegetarian", pescatarian: "pescatarian", "gluten-free": "gluten-free",
  "dairy-free": "dairy-free", "egg-free": "egg-free", "nut-free": "nut-free", "soy-free": "soy-free",
  "low-fodmap": "low-FODMAP", "low-histamine": "low-histamine", "diabetic-friendly": "low-GI / diabetic-friendly",
  "renal-friendly": "kidney-friendly",
};

export default function DietBadges({ recipe, canEdit, onChanged, signedIn = false, cookbooks = [] }) {
  const [busy, setBusy] = useState(false);
  const [copyFor, setCopyFor] = useState(null);
  const swaps = recipe.diet_badges_swaps || {};
  const active = recipe.diet_badges || [];
  const removed = canEdit ? (recipe.diet_badges_auto || []).filter((b) => !active.includes(b)) : [];
  const why = Object.entries(recipe.diet_badges_why || {}).filter(([b]) => DIET_LABELS[b]);
  if (!active.length && !removed.length && !why.length) return null;

  const applySwap = async (slug) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/diet-swap`, { slug, ingredient: swaps[slug].ingredient });
      onChanged?.(data);
      toast.success(`Swapped “${data.applied.ingredient}” for “${data.applied.swap}”`);
      track("diet_swap", { diet: slug });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply that swap");
    } finally {
      setBusy(false);
    }
  };

  const edit = async (body) => {
    try {
      const { data } = await api.patch(`/recipes/${recipe.recipe_id}/diet-badges`, body);
      onChanged?.(data);
      toast.success(body.remove ? `Removed “${DIET_LABELS[body.remove]}”` : `Restored “${DIET_LABELS[body.restore]}”`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update diet badges");
    }
  };

  return (
    <div className="mt-4" data-testid="recipe-diet-badges">
      <ul className="flex flex-wrap gap-1.5" aria-label="Diet badges">
        {active.map((b) => (
          <li key={b} className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-600/30 text-emerald-900 text-xs font-bold" data-testid={`diet-badge-${b}`}>
            <Link to={dietHref(b)} className="inline-flex items-center gap-1 pl-2.5 pr-2 py-1 hover:underline">
              <Leaf size={12} weight="fill" aria-hidden="true" /> {DIET_LABELS[b] || b}
            </Link>
            {canEdit && (
              <button type="button" onClick={() => edit({ remove: b })} aria-label={`Remove ${DIET_LABELS[b]} badge`} data-testid={`diet-badge-remove-${b}`} className="pr-2 pl-0.5 py-1 text-emerald-900/60 hover:text-red-700">
                <X size={11} weight="bold" />
              </button>
            )}
          </li>
        ))}
        {removed.map((b) => (
          <li key={b}>
            <button type="button" onClick={() => edit({ restore: b })} data-testid={`diet-badge-restore-${b}`} className="inline-flex items-center gap-1 rounded-full border border-dashed border-rf-ink/25 text-rf-ink-soft text-xs font-semibold px-2.5 py-1 line-through hover:no-underline hover:border-rf-ink/50" title="Removed by you — tap to restore">
              <ArrowCounterClockwise size={11} weight="bold" aria-hidden="true" /> {DIET_LABELS[b] || b}
            </button>
          </li>
        ))}
      </ul>
      {why.length > 0 && (
        <details className="mt-2 group" data-testid="diet-badges-why">
          <summary className="text-xs font-bold text-rf-ink-soft cursor-pointer hover:text-rf-ink select-none" data-testid="diet-badges-why-toggle">
            Why some badges are missing
          </summary>
          <ul className="mt-1.5 space-y-0.5 text-xs text-rf-ink-soft">
            {why.map(([b, blocker]) => (
              <li key={b} data-testid={`diet-why-${b}`}>
                <span className="font-bold text-rf-ink">Not {SHORT_WHY[b] || DIET_LABELS[b].toLowerCase()}:</span> {blocker}
                {swaps[b] && (
                  <span data-testid={`diet-swap-${b}`}>
                    {" "}→ try <b className="text-emerald-800">{swaps[b].swap}</b>{swaps[b].tip ? ` (${swaps[b].tip})` : ""}
                    {canEdit && (
                      <button type="button" disabled={busy} onClick={() => applySwap(b)} data-testid={`diet-swap-apply-${b}`}
                        className="ml-2 inline-flex items-center rounded-full border border-emerald-700/40 text-emerald-800 font-bold px-2 py-0.5 hover:bg-emerald-50 disabled:opacity-50">
                        Apply swap
                      </button>
                    )}
                  </span>
                )}
                {swaps[b] && signedIn && copyFor !== b && (
                  <button type="button" onClick={() => setCopyFor(b)} data-testid={`diet-copy-open-${b}`}
                    className="ml-2 inline-flex items-center rounded-full bg-emerald-700 text-white font-bold px-2 py-0.5 hover:bg-emerald-800">
                    Make a {SHORT_LABELS[b] || b} copy
                  </button>
                )}
                {copyFor === b && <DietCopyPanel recipe={recipe} slug={b} cookbooks={cookbooks} onClose={() => setCopyFor(null)} />}
              </li>
            ))}
          </ul>
        </details>
      )}
      <p className="text-[11px] text-rf-ink-soft mt-1.5" data-testid="diet-badges-note">
        Based on listed ingredients — always check labels for allergies.{canEdit ? " Tap × to remove a badge that's wrong." : ""}
      </p>
    </div>
  );
}
